export type MembroAprisionavel =
  | 'bracoEsquerdo'
  | 'bracoDireito'
  | 'antebracoEsquerdo'
  | 'antebracoDireito'
  | 'coxaEsquerda'
  | 'coxaDireita'
  | 'pernaEsquerda'
  | 'pernaDireita'
  | 'peEsquerdo'
  | 'peDireito'

export type TipoAprisionamento = 'pedal' | 'painel' | 'bancoColunaB' | 'entreEstruturas'

export interface ObservacaoAprisionamento {
  segmento: MembroAprisionavel
  alvos: readonly string[]
  duracaoContatoS: number
  velocidadeRelativaMs: number
}

export interface EvidenciaAprisionamento {
  segmento: MembroAprisionavel
  membro: string
  tipo: TipoAprisionamento
  alvos: readonly string[]
  leitura: string
}

const NOMES: Record<MembroAprisionavel, string> = {
  bracoEsquerdo: 'braço esquerdo',
  bracoDireito: 'braço direito',
  antebracoEsquerdo: 'antebraço esquerdo',
  antebracoDireito: 'antebraço direito',
  coxaEsquerda: 'coxa esquerda',
  coxaDireita: 'coxa direita',
  pernaEsquerda: 'perna esquerda',
  pernaDireita: 'perna direita',
  peEsquerdo: 'pé esquerdo',
  peDireito: 'pé direito',
}

const contem = (alvos: readonly string[], trecho: RegExp) =>
  alvos.some((alvo) => trecho.test(alvo.toLocaleLowerCase('pt-BR')))

/**
 * Reconhece aprisionamento POSSÍVEL a partir de contato sustentado e baixa
 * velocidade. Contato isolado não basta: o membro precisa permanecer apoiado
 * contra uma zona de retenção ou entre duas superfícies.
 */
export function avaliarPossivelAprisionamento(
  observacao: ObservacaoAprisionamento,
): EvidenciaAprisionamento | null {
  const { segmento, alvos, duracaoContatoS, velocidadeRelativaMs } = observacao
  if (alvos.length === 0 || velocidadeRelativaMs > 3.5) return null

  const ehPe = segmento === 'peEsquerdo' || segmento === 'peDireito'
  const ehPerna =
    ehPe ||
    segmento === 'coxaEsquerda' ||
    segmento === 'coxaDireita' ||
    segmento === 'pernaEsquerda' ||
    segmento === 'pernaDireita'
  const ehBraco = !ehPerna
  const pedal = contem(alvos, /pedal|pedaleira/)
  const painel = contem(alvos, /painel|parede corta-fogo/)
  const colunaB = contem(alvos, /coluna b/)
  const bancoOuPorta = contem(alvos, /banco|encosto|assento|porta/)
  const entreDuas = new Set(alvos.map((alvo) => alvo.toLocaleLowerCase('pt-BR'))).size >= 2

  let tipo: TipoAprisionamento | null = null
  if (ehPe && pedal && duracaoContatoS >= 0.06) tipo = 'pedal'
  else if (ehPerna && (painel || pedal) && duracaoContatoS >= 0.09) tipo = 'painel'
  else if (ehBraco && colunaB && (bancoOuPorta || duracaoContatoS >= 0.14)) tipo = 'bancoColunaB'
  else if (entreDuas && duracaoContatoS >= 0.12) tipo = 'entreEstruturas'
  if (!tipo) return null

  const membro = NOMES[segmento]
  const local =
    tipo === 'pedal'
      ? 'na pedaleira'
      : tipo === 'painel'
        ? 'sob o painel'
        : tipo === 'bancoColunaB'
          ? 'entre o banco e a coluna B'
          : 'entre estruturas'

  return {
    segmento,
    membro,
    tipo,
    alvos: [...alvos],
    leitura:
      `Possível aprisionamento do ${membro} ${local}: contato sustentado com ` +
      `${alvos.join(' e ')}. Confirme folgas e libere o membro antes de movimentá-lo; ` +
      'a simulação indica mecanismo, não diagnóstico de lesão.',
  }
}
